import NiceModal, { muiDialogV5, NiceModalHocProps, useModal } from '@ebay/nice-modal-react';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
    Backdrop,
    Box,
    Button,
    Dialog,
    DialogProps,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    OutlinedInput,
    Typography,
} from '@mui/material';
import { useRendererContext } from '@turiya/orchestrator-renderer-react';
import { ChangeEvent, useMemo, useState } from 'react';
import { NotificationManager } from 'react-notifications';
import { SFTP_PASSWORD_CHANGE_MESSAGE } from '../../common/model';
import { useClientId } from '../../components/auth/useClientid';

export const SFTPModal: React.FC<DialogProps & NiceModalHocProps> = NiceModal.create<DialogProps>(() => {
    const modal = useModal();
    const { clientId } = useClientId();

    const [userName, setUserName] = useState<string | undefined>(undefined);
    const [oldPassword, setOldPassword] = useState<string | undefined>(undefined);
    const [newPassword, setNewPassword] = useState<string | undefined>(undefined);

    const { getEngineApi } = useRendererContext();
    const [requestInProgress, setRequestInProgress] = useState<boolean>(false);

    const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
    const [showNewPassword, setShowNewPassword] = useState<boolean>(false);

    const handleClickShowOldPassword = (): void => setShowOldPassword((show) => !show);
    const handleClickShowNewPassword = (): void => setShowNewPassword((show) => !show);

    const handleUsernameChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setUserName(event.target.value);
    };

    const handleOldPasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setOldPassword(event.target.value);
    };

    const handleNewPasswordChange = (event: ChangeEvent<HTMLInputElement>): void => {
        setNewPassword(event.target.value);
    };

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>): void => {
        event.preventDefault();
    };

    const close = useMemo(() => {
        return (): Promise<unknown> => modal.hide();
    }, [modal]);

    const currentPasswordSameAsNew =
        oldPassword && newPassword
            ? oldPassword?.length > 0 && newPassword?.length > 0 && oldPassword === newPassword
            : false;

    const changePasswordClickHandler = async (): Promise<void> => {
        try {
            setRequestInProgress(true);
            if (clientId)
                await getEngineApi?.()?.postMessage(SFTP_PASSWORD_CHANGE_MESSAGE, {
                    userName: userName,
                    currentPassword: oldPassword,
                    newPassword: newPassword,
                    clientName: clientId,
                });
            NotificationManager.success('Password changed successfully');
            close();
        } catch (e) {
            if (e instanceof Error) {
                NotificationManager.error(`Failed to change Password due to ${e.message}`);
            }
        } finally {
            setRequestInProgress(false);
        }
    };

    return (
        <Dialog {...muiDialogV5(modal)}>
            {modal.visible ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-evenly',
                        padding: '4em',
                        alignItems: 'center',
                        gap: '10px',
                    }}>
                    <Typography
                        variant={'h2'}
                        sx={{ marginBottom: '0.6em', color: '#2A3B75', fontWeight: 700, fontSize: '1.6rem' }}>
                        Change SFTP Password
                    </Typography>
                    <Box sx={{ fontSize: '18px', textAlign: 'center', wordBreak: 'normal' }}>
                        Enter following details{' '}
                    </Box>

                    <FormControl sx={{ m: 1, width: '25ch' }} variant={'outlined'}>
                        <InputLabel>Username</InputLabel>
                        <OutlinedInput onChange={handleUsernameChange} label={'Username'} />
                    </FormControl>

                    <FormControl sx={{ m: 1, width: '25ch' }} variant={'outlined'}>
                        <InputLabel htmlFor={'outlined-adornment-password'}>Current Password</InputLabel>
                        <OutlinedInput
                            onChange={handleOldPasswordChange}
                            type={showOldPassword ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position={'end'}>
                                    <IconButton
                                        aria-label={'toggle password visibility'}
                                        onClick={handleClickShowOldPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge={'end'}>
                                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label={'Current Password'}
                        />
                    </FormControl>
                    <FormControl sx={{ m: 1, width: '25ch' }} variant={'outlined'}>
                        <InputLabel htmlFor={'outlined-adornment-password'}>New Password</InputLabel>
                        <OutlinedInput
                            onChange={handleNewPasswordChange}
                            type={showNewPassword ? 'text' : 'password'}
                            endAdornment={
                                <InputAdornment position={'end'}>
                                    <IconButton
                                        aria-label={'toggle password visibility'}
                                        onClick={handleClickShowNewPassword}
                                        onMouseDown={handleMouseDownPassword}
                                        edge={'end'}>
                                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            }
                            label={'New Password'}
                        />
                    </FormControl>
                    {currentPasswordSameAsNew ? (
                        <Typography sx={{ color: 'red', fontSize: '12px' }}>
                            Current and New Password cannot be same
                        </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', gap: '10px' }}>
                        <Button
                            disabled={(!userName && !newPassword) || currentPasswordSameAsNew}
                            onClick={changePasswordClickHandler} //api call
                            variant={'contained'}
                            sx={{
                                mt: 4,
                                width: '10em',
                                borderRadius: '0.4em',
                                background:
                                    !userName && !newPassword
                                        ? '#D3D3D3'
                                        : 'linear-gradient(92.76deg, #FF7C91 1.88%, #EB5A72 97.88%)',
                                textAlign: 'center',
                            }}>
                            Change
                        </Button>
                        <Button
                            onClick={close}
                            variant={'contained'}
                            sx={{
                                mt: 4,
                                width: '10em',
                                borderRadius: '0.4em',
                                background: 'linear-gradient(92.76deg, #FF7C91 1.88%, #EB5A72 97.88%)',
                                textAlign: 'center',
                            }}>
                            Close
                        </Button>
                    </Box>

                    <Backdrop open={requestInProgress} />
                </Box>
            ) : null}
        </Dialog>
    );
});
